/**
 * settlement_archetypes.ts — pick the engine city-archetype for a settlement.
 *
 * 2026-08-31 drift fix (The Unbuilt Town wave): cities were hardcoded to
 * 'cyberpunk_district' — an id the engine never registered, so getArchetype
 * silently fell back to 'grid' and EVERY city in the world was the legacy grid
 * generator (the 2026-07-20 sweep that "chose" it was measuring the fallback).
 * The re-run sweep (r420, 3 seed jitters) shows today's engine closes real
 * fabric on several archetypes:
 *   grid 95–108 blocks / 278–433 pads (seed-VARIED) ·
 *   cyberpunk_city 224 / 448 (dense lattice, seed-INVARIANT roads) ·
 *   ring_station 49 / 533–543 (radial, seed-varied).
 * One id for all cities is a sameness trap either way, so cities now draw
 * from a POOL of the three healthy forms — lattice, dense grid, and radial —
 * per siteSeed. Towns keep the radial variety (21–77 pads at r180); hamlets
 * use the two that still form at r95. Pure function → every client agrees.
 */
import type { SettlementSite } from '../../../shared/settlements.js';

const CITY_ARCHETYPES = ['grid', 'cyberpunk_city', 'ring_station'] as const;
const TOWN_ARCHETYPES = ['dome_colony', 'spaceport_hub', 'orbital_citadel', 'ring_station', 'frontier_outpost'] as const;
const HAMLET_ARCHETYPES = ['frontier_outpost', 'dome_colony'] as const;

/** Deterministic engine-archetype id for a settlement site. */
export function archetypeForSite(tier: SettlementSite['tier'], siteSeed: number): string {
  // Capital wave: districts (and the never-generated capital config) draw from the
  // city pool; the campus plaza from the town pool.
  const pool = tier === 'city' || tier === 'district' || tier === 'capital' ? CITY_ARCHETYPES
    : tier === 'town' || tier === 'circuit' ? TOWN_ARCHETYPES : HAMLET_ARCHETYPES;
  return pool[siteSeed % pool.length];
}
